import { useState } from "react";
import { Box, Checkbox, FormControl, Grid, InputLabel, Link, ListItemText, MenuItem, Select, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { addDays, format, startOfMonth, subDays, subMonths } from "date-fns";
import { pt } from "date-fns/locale";
import { getItems } from "../../../api/items";
import { useFields } from "../../../api/useFields";
import { useUser } from "../../../api/useAuth";
import { dailyCountsFromGroupedRows, dailyPointsFromCountMap, monthlyCountsFromGroupedRows, singleCount } from "../../../dashboard-framework/aggregateRows";
import { DASHBOARD_COLORS } from "../../../dashboard-framework/colors";
import { formatQuantity } from "../../../dashboard-framework/format";
import { useDashboardAccess } from "../../../dashboard-framework/DashboardAccessProvider";
import { DashboardDateFilter } from "../../../dashboard-framework/DashboardDateFilter";
import { resolveDatePreset, resolveRequiredDateFilterRange } from "../../../dashboard-framework/datePresets";
import { GlobalFilterValues } from "../../../dashboard-framework/types";
import { drillDownFromAggregate } from "../../../dashboard-framework/drilldown";
import { getChipLabel } from "../../../components/chips/CustomChip";
import { LEADS_DRILL_FIELDS, leadRoute, leadsDrillColumns, SESSIONS_DRILL_FIELDS, sessionRoute, sessionsDrillColumns } from "../shared/drillColumns";
import { UpcomingEvaluationsGauges } from "../shared/UpcomingEvaluationsGauges";
import { formatDateRangePt, treatmentStatusListPt } from "../../../dashboard-framework/describeWidgetLogic";
import { DashboardPage } from "../../../dashboard-framework/DashboardPage";
import { useAggregate } from "../../../dashboard-framework/useAggregate";
import { useGlobalFilterValues } from "../../../dashboard-framework/useGlobalFilterValues";
import { DataTableCard } from "../../../dashboard-framework/widgets/DataTableCard";
import { GaugeCard } from "../../../dashboard-framework/widgets/GaugeCard";
import { PivotTableCard } from "../../../dashboard-framework/widgets/PivotTableCard";
import { StackedBarChart } from "../../../dashboard-framework/widgets/StackedBarChart";
import { StatCard } from "../../../dashboard-framework/widgets/StatCard";
import { TimeSeriesChart } from "../../../dashboard-framework/widgets/TimeSeriesChart";
import { WidgetCard } from "../../../dashboard-framework/widgets/WidgetCard";
import { ScheduleCallsBarChart } from "./components/ScheduleCallsBarChart";
import {
    bookingsByPersonQuery,
    CALL_TEAM_LICENCE_FIELDS,
    callsByOperatorQuery,
    callsPeriodByDayQuery,
    CALLS_OPERATOR_FIELDS,
    CONVERSATION_HOURS_CHART_DAYS,
    CRM_LEAD_LINK_PREFIX,
    DAILY_BOOKINGS_TARGET,
    dailyFirstVisitBookingsQuery,
    DETAIL_DATE_PRESETS,
    DETAIL_DEFAULT_DATE_PRESET,
    DETAIL_DEFAULT_MIN_MINUTES,
    DETAIL_MINUTES_THRESHOLD_OPTIONS,
    firstVisitAttendanceQuery,
    leadsByStatusQuery,
    LEADS_STATUS_DATE_PRESETS,
    LEADS_STATUS_DEFAULT_DATE_PRESET,
    LEADS_STATUS_DEFAULT_SELECTION,
    LEADS_WITHOUT_STATUS_VALUE,
    LEADS_WITHOUT_STATUS_LABEL,
    monthlyLeadsQuery,
    receptionKpisGlobalFilters,
    RECEPTION_EXCLUDED_USER_IDS,
    RECEPTION_PERSON_VIEWER_PARAM,
    RETURN_LEADS_FILTER,
    RETURN_LEADS_SINCE,
    RETURN_WINDOWS_FULL_SCHEDULE,
    RETURN_WINDOWS_STANDARD,
    SCHEDULE_FIELDS,
    sessionsAttendanceQuery,
    WAITING_TREATMENT_STATUSES,
    weeklyFirstVisitBookingsQuery,
    WEEKLY_BOOKINGS_GOAL,
} from "./receptionKpisConfig";
import {
    attendanceRateFromStatusRows,
    averageBusinessMinutes,
    avgDailyConversationByPerson,
    bookingRowsTotal,
    callRowsTotal,
    contactDetailRows,
    contactLeadsAboveThreshold,
    leadsEfficiencyPoints,
    leadStatusRows,
    leadStatusSelectionFilter,
    marcadasByPersonKey,
    perPersonBookingRows,
    perPersonCallRows,
    scheduleCallsByPersonDayPoints,
    waitingTreatmentItems,
    waitingTreatmentsPivot,
} from "./receptionKpisRows";

const EFFICIENCY_MONTHS_BACK = 12;

// A rota /users ignora limit/page e responde com ≤100 registos — lookup por blocos de ids.
const PEOPLE_LOOKUP_CHUNK_SIZE = 100;
const PEOPLE_FIELDS = "id,first_name,last_name";

const fetchPeopleByIds = async (personIds: Array<string>) => {
    const idChunks: Array<Array<string>> = [];
    for (let chunkStart = 0; chunkStart < personIds.length; chunkStart += PEOPLE_LOOKUP_CHUNK_SIZE) {
        idChunks.push(personIds.slice(chunkStart, chunkStart + PEOPLE_LOOKUP_CHUNK_SIZE));
    }
    const chunkResults = await Promise.all(
        idChunks.map((idChunk) => getItems("directus_users", PEOPLE_FIELDS, { filter: { id: { _in: idChunk } }, sort: "first_name" }))
    );
    const people = chunkResults.flatMap((chunkResult) => chunkResult.items);
    return people;
};

export const ReceptionKpisDashboard = () => {
    const { values, setValue } = useGlobalFilterValues(receptionKpisGlobalFilters);
    const access = useDashboardAccess();
    const userResult = useUser();

    const now = new Date();
    const weekRange = resolveDatePreset("esta_semana", now);
    const timelineRange = resolveRequiredDateFilterRange(values, "timeline", "este_mes", now);
    const efficiencyStartMonth = startOfMonth(subMonths(now, EFFICIENCY_MONTHS_BACK));
    const efficiencyStartISO = format(efficiencyStartMonth, "yyyy-MM-dd");

    // Filtros locais da análise detalhada (não seguem o "Período" global): janela de criação das
    // leads (DashboardDateFilter partilhado — presets + "Personalizado" De/Até) + limiar de minutos
    // úteis (réplica do slider "Minutos" do Zoho). Enquanto o range personalizado está incompleto,
    // o resolve mantém o último range válido (fallback: últimos 30 dias).
    const [detailFilterValues, setDetailFilterValues] = useState<GlobalFilterValues>({ detailCriacao: DETAIL_DEFAULT_DATE_PRESET });
    const setDetailFilterValue = (filterKey: string, value: string | number | null) =>
        setDetailFilterValues((previousValues) => ({ ...previousValues, [filterKey]: value }));
    const [detailMinMinutes, setDetailMinMinutes] = useState<number | null>(DETAIL_DEFAULT_MIN_MINUTES);
    const [leadsStatusFilterValues, setLeadsStatusFilterValues] = useState<GlobalFilterValues>({
        leadsStatusCriacao: LEADS_STATUS_DEFAULT_DATE_PRESET,
    });
    const setLeadsStatusFilterValue = (filterKey: string, value: string | number | null) =>
        setLeadsStatusFilterValues((previousValues) => ({ ...previousValues, [filterKey]: value }));
    const [selectedLeadStatuses, setSelectedLeadStatuses] = useState<Array<string>>(LEADS_STATUS_DEFAULT_SELECTION);
    const leadsStatusRange = resolveRequiredDateFilterRange(
        leadsStatusFilterValues,
        "leadsStatusCriacao",
        LEADS_STATUS_DEFAULT_DATE_PRESET,
        now
    );
    const detailRange = resolveRequiredDateFilterRange(detailFilterValues, "detailCriacao", DETAIL_DEFAULT_DATE_PRESET, now);

    // Scoping por pessoa APENAS da secção de produtividade (o funil acima é global para todos os que
    // abrem o dashboard): gestão (viewer_rules com params {}) vê toda a equipa; os restantes caem na
    // regra própria (params.callUserId = "$current_user") e cada query filtra por user_created.
    const currentUserId: string | null = userResult.data?.id || null;
    const personViewerParam = access.viewerParams?.[RECEPTION_PERSON_VIEWER_PARAM];
    const scopedUserId = personViewerParam === undefined ? null : personViewerParam === "$current_user" ? currentUserId : String(personViewerParam);
    const isOwnProductivityView = personViewerParam !== undefined;
    const productivityPending = isOwnProductivityView && scopedUserId === null;
    const personScopeFilter = scopedUserId ? { user_created: { _eq: scopedUserId } } : undefined;

    const weeklyBookingsFilter = { date_created: { _gte: weekRange.gte, _lt: weekRange.lt } };
    const weeklyBookingsResult = useAggregate(weeklyFirstVisitBookingsQuery, { filter: weeklyBookingsFilter });
    const dailyBookingsResult = useAggregate(dailyFirstVisitBookingsQuery, {
        filter: { date_created: { _gte: timelineRange.gte, _lt: timelineRange.lt } },
    });
    const firstVisitAttendanceResult = useAggregate(firstVisitAttendanceQuery, {
        filter: { date_created: { _gte: timelineRange.gte, _lt: timelineRange.lt } },
    });
    const sessionsAttendanceResult = useAggregate(sessionsAttendanceQuery, {
        filter: { date_created: { _gte: timelineRange.gte, _lt: timelineRange.lt } },
    });
    const monthlyLeadsResult = useAggregate(monthlyLeadsQuery, { filter: { date_created: { _gte: efficiencyStartISO } } });
    const leadsStatusDateFilter = { date_created: { _gte: leadsStatusRange.gte, _lt: leadsStatusRange.lt } };
    const leadsStatusSelectionClause = leadStatusSelectionFilter(selectedLeadStatuses, LEADS_WITHOUT_STATUS_VALUE);
    const leadsStatusFilter = leadsStatusSelectionClause ? { _and: [leadsStatusDateFilter, leadsStatusSelectionClause] } : leadsStatusDateFilter;
    const leadsByStatusResult = useAggregate(leadsByStatusQuery, { filter: leadsStatusFilter, enabled: leadsStatusSelectionClause !== null });
    const { formatedFields: leadFields } = useFields("leads");

    const waitingTreatmentsResult = useQuery({
        queryKey: ["reception-waiting-treatments", WAITING_TREATMENT_STATUSES],
        queryFn: () =>
            getItems("treatments", "id,date_created,treatment_status_new,client.client_id", {
                filter: { treatment_status_new: { _in: WAITING_TREATMENT_STATUSES } },
                pageSize: 1000,
                sort: "-date_created",
            }),
        staleTime: 5 * 60 * 1000,
    });

    const returnLeadsResult = useQuery({
        queryKey: ["reception-return-leads", RETURN_LEADS_SINCE, timelineRange.gte, timelineRange.lt],
        queryFn: () =>
            getItems("leads", "id,date_created,first_contact_attempt", {
                filter: { _and: [RETURN_LEADS_FILTER, { date_created: { _gte: timelineRange.gte, _lt: timelineRange.lt } }] },
                pageSize: 3000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });
    const detailLeadsResult = useQuery({
        queryKey: ["reception-detail-leads", detailRange.gte, detailRange.lt],
        queryFn: () =>
            getItems("leads", "id,date_created,first_contact_attempt", {
                filter: { date_created: { _gte: detailRange.gte, _lt: detailRange.lt } },
                pageSize: 3000,
                sort: "-date_created",
            }),
        staleTime: 5 * 60 * 1000,
    });
    const evaluationsResult = useQuery({
        queryKey: ["reception-cohort-evaluations", efficiencyStartISO],
        queryFn: () =>
            getItems("treatments", "id,client.lead.date_created", {
                filter: { client: { lead: { date_created: { _gte: efficiencyStartISO } } } },
                pageSize: 5000,
                sort: "id",
            }),
        staleTime: 5 * 60 * 1000,
    });

    // === Produtividade por pessoa (período = filtro global; gráfico = últimos N dias) ===
    const excludedUsersFilter = { user_created: { _nin: RECEPTION_EXCLUDED_USER_IDS } };
    const productivityPeriodFilter = { date_created: { _gte: timelineRange.gte, _lt: timelineRange.lt } };
    const productivityScopedFilter = personScopeFilter
        ? { _and: [productivityPeriodFilter, excludedUsersFilter, personScopeFilter] }
        : { _and: [productivityPeriodFilter, excludedUsersFilter] };
    const bookingsByPersonResult = useAggregate(bookingsByPersonQuery, { filter: productivityScopedFilter, enabled: !productivityPending });
    const callsByOperatorResult = useAggregate(callsByOperatorQuery, { filter: productivityScopedFilter, enabled: !productivityPending });
    // Gráfico de média diária — só gestão (não corre na vista própria de um agente).
    const callsPeriodByDayResult = useAggregate(callsPeriodByDayQuery, {
        filter: productivityScopedFilter,
        enabled: !productivityPending && !isOwnProductivityView,
    });

    const conversationStartISO = format(subDays(now, CONVERSATION_HOURS_CHART_DAYS - 1), "yyyy-MM-dd");
    const conversationEndISO = format(addDays(now, 1), "yyyy-MM-dd");
    const conversationCallsFilter = personScopeFilter
        ? { _and: [{ date_created: { _gte: conversationStartISO } }, excludedUsersFilter, personScopeFilter] }
        : { _and: [{ date_created: { _gte: conversationStartISO } }, excludedUsersFilter] };
    const conversationCallsResult = useQuery({
        queryKey: ["reception-conversation-calls", conversationStartISO, scopedUserId],
        queryFn: () => getItems("calls", CALLS_OPERATOR_FIELDS, { filter: conversationCallsFilter, pageSize: 5000, sort: "-date_created" }),
        enabled: !productivityPending,
        staleTime: 5 * 60 * 1000,
    });
    // Horário lançado (schedules) é por `user` (não user_created) — filtro de scope próprio.
    const conversationSchedulesWindow = { start_date: { _gte: conversationStartISO, _lt: conversationEndISO } };
    const conversationSchedulesFilter = scopedUserId
        ? { _and: [conversationSchedulesWindow, { user: { _eq: scopedUserId } }] }
        : conversationSchedulesWindow;
    const conversationSchedulesResult = useQuery({
        queryKey: ["reception-conversation-schedules", conversationStartISO, scopedUserId],
        queryFn: () => getItems("schedules", SCHEDULE_FIELDS, { filter: conversationSchedulesFilter, pageSize: 5000, sort: "start_date" }),
        enabled: !productivityPending,
        staleTime: 5 * 60 * 1000,
    });
    // Roster da equipa (licenças call center): inclui no gráfico quem tem horário mesmo sem chamadas.
    const callTeamResult = useQuery({
        queryKey: ["reception-call-team-roster"],
        queryFn: () => getItems("call_center_licenses", CALL_TEAM_LICENCE_FIELDS, { pageSize: 100, sort: "id" }),
        staleTime: 30 * 60 * 1000,
    });

    const productivityPersonIds = Array.from(
        new Set(
            [
                ...(callsByOperatorResult.data || []).map((row: { user_created: string | null }) => row.user_created),
                ...(bookingsByPersonResult.data || []).map((row: { user_created: string | null }) => row.user_created),
            ].filter(Boolean) as Array<string>
        )
    ).sort();
    const productivityPersonIdsKey = productivityPersonIds.join(",");
    const productivityPeopleResult = useQuery({
        queryKey: ["reception-productivity-people", productivityPersonIdsKey],
        queryFn: () => fetchPeopleByIds(productivityPersonIds),
        enabled: productivityPersonIds.length > 0,
        staleTime: 5 * 60 * 1000,
    });

    const weeklyBookings = singleCount(weeklyBookingsResult.data);
    const tomorrowISO = format(addDays(now, 1), "yyyy-MM-dd");
    const dailyPoints = dailyPointsFromCountMap(
        dailyCountsFromGroupedRows(dailyBookingsResult.data || [], "date_created"),
        { gte: timelineRange.gte, lt: tomorrowISO < timelineRange.lt ? tomorrowISO : timelineRange.lt },
        "bookings"
    );
    const firstVisitAttendance = attendanceRateFromStatusRows(firstVisitAttendanceResult.data || []);
    const sessionsAttendance = attendanceRateFromStatusRows(sessionsAttendanceResult.data || []);

    const returnLeads = returnLeadsResult.data?.items || [];
    const averageStandard = averageBusinessMinutes(returnLeads, RETURN_WINDOWS_STANDARD);
    const averageFullSchedule = averageBusinessMinutes(returnLeads, RETURN_WINDOWS_FULL_SCHEDULE);

    const nowMonthIndex = now.getFullYear() * 12 + now.getMonth();
    const efficiencyPoints = leadsEfficiencyPoints(
        monthlyCountsFromGroupedRows(monthlyLeadsResult.data || [], "date_created"),
        evaluationsResult.data?.items || [],
        nowMonthIndex - EFFICIENCY_MONTHS_BACK,
        nowMonthIndex
    );

    const waitingPivot = waitingTreatmentsPivot(waitingTreatmentItems(waitingTreatmentsResult.data?.items || []));

    const leadStatusField = leadFields.find((field) => field.field === "status");
    const leadStatusLabel = (status: string) => (leadStatusField ? getChipLabel(leadStatusField, status) || status : status);
    const leadsStatusTableRows = leadsStatusSelectionClause ? leadStatusRows(leadsByStatusResult.data || [], leadStatusLabel) : [];
    const leadStatusOptions = [
        ...(leadStatusField?.settings?.options?.choices || []).map((choice) => ({ value: choice.value || "", label: choice.text || choice.value || "" })),
        { value: LEADS_WITHOUT_STATUS_VALUE, label: LEADS_WITHOUT_STATUS_LABEL },
    ];
    const selectedLeadStatusesLabel = (values: Array<string>) =>
        values.map((value) => (value === LEADS_WITHOUT_STATUS_VALUE ? LEADS_WITHOUT_STATUS_LABEL : leadStatusLabel(value))).join(", ");
    const leadsStatusTotal = leadsStatusTableRows.reduce((runningTotal, row) => runningTotal + row.leads, 0);

    const detailFilteredLeads = contactLeadsAboveThreshold(detailLeadsResult.data?.items || [], RETURN_WINDOWS_STANDARD, detailMinMinutes);
    const detailRows = contactDetailRows(detailFilteredLeads, RETURN_WINDOWS_STANDARD, CRM_LEAD_LINK_PREFIX);
    const detailAverage = averageBusinessMinutes(detailFilteredLeads, RETURN_WINDOWS_STANDARD);

    const productivityNameById = Object.fromEntries(
        (productivityPeopleResult.data || []).map((person: { id: string; first_name?: string | null; last_name?: string | null }) => [
            person.id,
            [person.first_name, person.last_name].filter(Boolean).join(" ").replace(/\s+/g, " ").trim(),
        ])
    );
    const bookingRows = perPersonBookingRows(bookingsByPersonResult.data || [], productivityNameById);
    const callRows = perPersonCallRows(callsByOperatorResult.data || [], marcadasByPersonKey(bookingRows), productivityNameById);
    const bookingTotals = bookingRowsTotal(bookingRows);
    const callTotals = callRowsTotal(callRows);

    const conversationDays = Array.from({ length: CONVERSATION_HOURS_CHART_DAYS }, (_unused, dayOffset) => {
        const dayDate = subDays(now, CONVERSATION_HOURS_CHART_DAYS - 1 - dayOffset);
        const conversationDay = { dayKey: format(dayDate, "yyyy-MM-dd"), label: format(dayDate, "EEE dd/MM", { locale: pt }) };
        return conversationDay;
    });
    const rosterUserIds = ((callTeamResult.data?.items || []) as Array<{ user?: { id?: string } | null }>)
        .map((licence) => licence.user?.id)
        .filter((userId): userId is string => Boolean(userId) && !RECEPTION_EXCLUDED_USER_IDS.includes(userId as string));
    const chartTeamUserIds = scopedUserId ? [scopedUserId] : rosterUserIds;
    const conversation = scheduleCallsByPersonDayPoints(
        conversationCallsResult.data?.items || [],
        conversationSchedulesResult.data?.items || [],
        chartTeamUserIds,
        conversationDays
    );
    const avgConversationRows = avgDailyConversationByPerson(callsPeriodByDayResult.data || [], productivityNameById);
    const avgConversationPoints = avgConversationRows.map((row) => ({ person: row.label, media: row.avgHours }));
    const avgConversationSeries = [{ key: "media", label: "Média h/dia", color: DASHBOARD_COLORS.toCome }];

    const productivityPeopleLoading = productivityPersonIds.length > 0 && productivityPeopleResult.isLoading;
    const bookingTableLoading = productivityPending || bookingsByPersonResult.isLoading || productivityPeopleLoading;
    const callTableLoading = productivityPending || callsByOperatorResult.isLoading || productivityPeopleLoading;
    const conversationLoading =
        productivityPending || conversationCallsResult.isLoading || conversationSchedulesResult.isLoading || callTeamResult.isLoading;
    const avgConversationLoading = productivityPending || callsPeriodByDayResult.isLoading || productivityPeopleLoading;
    const productivityScopeNote = isOwnProductivityView ? "Mostra apenas os seus números." : "Mostra toda a equipa de receção.";

    const returnFiltersText = `Considera leads criadas ${formatDateRangePt(timelineRange)} (nunca antes de 24/01/2025, quando o registo da primeira tentativa começou), excluindo o canal "chat" (sem canal conta), as leads 20376/20425 e o dia 01/01/2026; leads sem primeira tentativa de contacto não entram na média.`;
    const windowsStandardText = "janelas úteis 10h–13h e 14h–17h, sem sábados nem domingos";
    const windowsFullText = "janelas úteis 10h–13h e 14h–19:15h, sem sábados nem domingos";

    const explanations = {
        weeklyBookings: {
            queryDescription: `Conta as sessões de primeira visita (sessões com lead associada, dos serviços Avaliação qEEG - Primeira ou Psicologia) cuja data de criação está ${formatDateRangePt(weekRange)} (semana de segunda a domingo).`,
        },
        returnStandard: {
            queryDescription: `Média, por lead, dos minutos úteis entre a criação da lead (hora de Lisboa) e a primeira tentativa de contacto, contando apenas ${windowsStandardText}. ${returnFiltersText}`,
        },
        returnFull: {
            queryDescription: `Média, por lead, dos minutos úteis entre a criação da lead (hora de Lisboa) e a primeira tentativa de contacto, contando apenas ${windowsFullText}. ${returnFiltersText}`,
        },
        firstVisitAttendance: {
            queryDescription: `Taxa de comparência das sessões de primeira visita (com lead associada, serviços Avaliação qEEG - Primeira ou Psicologia) CRIADAS ${formatDateRangePt(timelineRange)}: realizadas ÷ (realizadas + canceladas + faltas) × 100.`,
        },
        sessionsAttendance: {
            queryDescription: `Taxa de comparência das sessões de NeuroFeedback e Psicologia CRIADAS ${formatDateRangePt(timelineRange)}: realizadas ÷ (realizadas + canceladas + faltas) × 100.`,
        },
        dailyBookings: {
            queryDescription: `Conta, por dia de criação, as sessões de primeira visita (com lead associada, serviços Avaliação qEEG - Primeira ou Psicologia) criadas no período selecionado (${formatDateRangePt(timelineRange)}). A linha a tracejado marca o objetivo diário de ${DAILY_BOOKINGS_TARGET}.`,
        },
        efficiency: {
            queryDescription: `Para cada mês de criação das leads (últimos ${EFFICIENCY_MONTHS_BACK + 1} meses): % de leads que chegaram a avaliação = tratamentos ligados às leads desse mês (via cliente) ÷ leads criadas × 100.`,
        },
        waiting: {
            queryDescription: `Conta os tratamentos com estado ${treatmentStatusListPt(WAITING_TREATMENT_STATUSES)} (fotografia atual, sem filtro de data). Expanda cada estado por mês de criação do tratamento e depois por número de cliente; cada cliente abre o tratamento correspondente num novo separador.`,
        },
        leadsStatus: {
            queryDescription: `Conta as leads criadas ${formatDateRangePt(leadsStatusRange)}, agrupadas pelo estado atual (campo "Estado" da lead — o mesmo da grid de leads). ${
                selectedLeadStatuses.length === 0
                    ? "Nenhum estado selecionado — escolha pelo menos um."
                    : `Estados selecionados: ${selectedLeadStatusesLabel(selectedLeadStatuses)}.`
            } Por omissão mostra os estados em aberto (a lead ainda está nas mãos do call center), incluindo as leads sem estado ("${LEADS_WITHOUT_STATUS_LABEL}"). Clique numa linha para ver as leads desse estado. Filtros locais desta tabela, independentes do "Período" global.`,
            businessNote: "Mostra o estado ATUAL das leads da janela escolhida, não o estado que tinham na altura — serve para ver onde é que o call center tem leads paradas.",
        },
        detail: {
            queryDescription: `Leads criadas ${formatDateRangePt(detailRange)}, com a data de criação, a primeira tentativa de contacto e os minutos úteis até esse contacto (${windowsStandardText}). ${
                detailMinMinutes === null
                    ? "A mostrar todas as leads da janela."
                    : `A mostrar apenas as leads com mais de ${detailMinMinutes} minutos úteis até ao 1.º contacto — as leads ainda SEM 1.ª tentativa ("—") ficam sempre visíveis, porque são os piores casos.`
            } A "Média geral" é calculada sobre as linhas visíveis (ignora as sem contacto). Filtros locais desta tabela, independentes do "Período" global.`,
        },
        bookingsByPerson: {
            queryDescription: `Por pessoa que criou a marcação, sessões de 1.ª avaliação EEG (serviço qEEG Primeira) com data de criação ${formatDateRangePt(timelineRange)}: marcadas (todas) e realizadas (estado "realizada"). % Comparência = realizadas ÷ marcadas. ${productivityScopeNote}`,
        },
        callsByPerson: {
            queryDescription: `Por operador (utilizador que registou a chamada), com data ${formatDateRangePt(timelineRange)}: nº de chamadas, total de minutos de conversação e marcações de 1.ª EEG dessa pessoa. Chamadas/marcação e minutos/marcação só aparecem quando há chamadas e marcações. ${productivityScopeNote}`,
        },
        conversationHours: {
            queryDescription: `Por pessoa e por dia (hora de Lisboa), nos últimos ${CONVERSATION_HOURS_CHART_DAYS} dias (independente do período): a barra é a duração do horário lançado e o sub-segmento sólido são as horas de conversação (tempo de chamada ÷ 3600). Passe o rato para ver agendado vs chamadas e as notas do horário. Inclui a equipa de receção (com licença de call center) e quem fez chamadas. ${productivityScopeNote}`,
        },
        avgConversation: {
            queryDescription: `Média diária de horas de conversação por pessoa no período ${formatDateRangePt(timelineRange)}: total de horas de conversação ÷ número de dias em que fez chamadas. Vista de gestão (a equipa toda).`,
        },
    };

    return (
        <DashboardPage globalFilters={receptionKpisGlobalFilters} values={values} onChange={setValue}>
            <Box display="grid" gap={3} sx={{ gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" } }}>
                <StatCard
                    label="Média retorno 10h-13h / 14h-17h"
                    value={returnLeadsResult.data ? averageStandard ?? undefined : undefined}
                    color={DASHBOARD_COLORS.neutral}
                    explanation={explanations.returnStandard}
                    isLoading={returnLeadsResult.isLoading}
                />
                <StatCard
                    label="Média retorno 10h-13h / 14h-19:15h"
                    value={returnLeadsResult.data ? averageFullSchedule ?? undefined : undefined}
                    color={DASHBOARD_COLORS.neutral}
                    explanation={explanations.returnFull}
                    isLoading={returnLeadsResult.isLoading}
                />
                <StatCard
                    label="Taxa de comparência - 1.ª visita"
                    value={firstVisitAttendanceResult.data ? (firstVisitAttendance.ratePct === null ? undefined : `${firstVisitAttendance.ratePct}%`) : undefined}
                    color={DASHBOARD_COLORS.done}
                    explanation={explanations.firstVisitAttendance}
                    isLoading={firstVisitAttendanceResult.isLoading}
                />
                <StatCard
                    label="Taxa de comparência - Sessões"
                    value={sessionsAttendanceResult.data ? (sessionsAttendance.ratePct === null ? undefined : `${sessionsAttendance.ratePct}%`) : undefined}
                    color={DASHBOARD_COLORS.toCome}
                    explanation={explanations.sessionsAttendance}
                    isLoading={sessionsAttendanceResult.isLoading}
                />
            </Box>

            <Box display="grid" gap={3} sx={{ gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" } }}>
                <GaugeCard
                    title="Marcações esta semana"
                    value={weeklyBookingsResult.data ? weeklyBookings : undefined}
                    goal={WEEKLY_BOOKINGS_GOAL}
                    explanation={explanations.weeklyBookings}
                    isLoading={weeklyBookingsResult.isLoading}
                    isError={weeklyBookingsResult.isError}
                    drillDown={drillDownFromAggregate(weeklyFirstVisitBookingsQuery, weeklyBookingsFilter, {
                        title: "Marcações esta semana",
                        queryDescription: explanations.weeklyBookings.queryDescription,
                        fieldsQuery: SESSIONS_DRILL_FIELDS,
                        defaultSort: "start_date",
                        columns: sessionsDrillColumns,
                        rowRoute: sessionRoute,
                    })}
                />
                <UpcomingEvaluationsGauges showClientTypeChips={false} />
            </Box>

            <WidgetCard
                title="Primeiras visitas marcadas por dia (EEG ou Psicologia)"
                explanation={explanations.dailyBookings}
                bodyHeight={320}
                isLoading={dailyBookingsResult.isLoading}
                isError={dailyBookingsResult.isError}
            >
                <TimeSeriesChart
                    points={dailyPoints}
                    series={[{ key: "bookings", label: "Marcações", color: DASHBOARD_COLORS.done }]}
                    height={320}
                    referenceLine={{ value: DAILY_BOOKINGS_TARGET, label: "Objetivo diário", color: DASHBOARD_COLORS.cancelled }}
                />
            </WidgetCard>

            <WidgetCard
                title="Eficiência das leads (% leads → avaliações)"
                explanation={explanations.efficiency}
                bodyHeight={320}
                isLoading={monthlyLeadsResult.isLoading || evaluationsResult.isLoading}
                isError={monthlyLeadsResult.isError || evaluationsResult.isError}
            >
                <TimeSeriesChart
                    points={efficiencyPoints}
                    series={[{ key: "efficiencyPct", label: "% leads → avaliações", color: DASHBOARD_COLORS.highlight }]}
                    height={320}
                />
            </WidgetCard>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <DataTableCard
                        title="Estado das leads"
                        explanation={explanations.leadsStatus}
                        headerControls={
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <DashboardDateFilter
                                    label="Criação"
                                    filterKey="leadsStatusCriacao"
                                    values={leadsStatusFilterValues}
                                    onChange={setLeadsStatusFilterValue}
                                    presets={LEADS_STATUS_DATE_PRESETS}
                                />
                                <FormControl size="small" sx={{ minWidth: 200, maxWidth: 320 }}>
                                    <InputLabel id="leads-status-label">Estado</InputLabel>
                                    <Select
                                        labelId="leads-status-label"
                                        label="Estado"
                                        multiple
                                        value={selectedLeadStatuses}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            const nextValues = typeof value === "string" ? value.split(",") : value;
                                            setSelectedLeadStatuses(nextValues);
                                        }}
                                        renderValue={(selected) => selectedLeadStatusesLabel(selected as Array<string>)}
                                    >
                                        {leadStatusOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                <Checkbox checked={selectedLeadStatuses.includes(option.value)} size="small" />
                                                <ListItemText primary={option.label} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        }
                        columns={[
                            { key: "estado", label: "Estado" },
                            { key: "leads", label: "Leads", isNumeric: true },
                        ]}
                        rows={leadsStatusTableRows.map((row) => ({ estado: row.label, leads: formatQuantity(row.leads) }))}
                        totalsRow={{ estado: "Total", leads: formatQuantity(leadsStatusTotal) }}
                        maxBodyHeight={420}
                        isLoading={leadsByStatusResult.isLoading}
                        isError={leadsByStatusResult.isError}
                        rowDrillDown={(rowIndex) => {
                            const row = leadsStatusTableRows[rowIndex];
                            if (!row) return null;
                            const statusFilter = row.statusValue === null ? { status: { _null: true } } : { status: { _eq: row.statusValue } };
                            const drill = drillDownFromAggregate(leadsByStatusQuery, { _and: [leadsStatusFilter, statusFilter] }, {
                                title: `Leads — ${row.label}`,
                                fieldsQuery: LEADS_DRILL_FIELDS,
                                defaultSort: "-date_created",
                                columns: leadsDrillColumns.map((column) =>
                                    column.key === "status" ? { ...column, chipField: leadStatusField } : column
                                ),
                                rowRoute: leadRoute,
                                queryDescription: `Leads criadas ${formatDateRangePt(leadsStatusRange)} com o estado "${row.label}".`,
                            });
                            return drill;
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <PivotTableCard
                        title="Estado dos tratamentos em espera"
                        explanation={explanations.waiting}
                        pivot={waitingPivot}
                        rowHeaderLabel="Estado"
                        maxBodyHeight={420}
                        renderNodeLabel={(node) =>
                            node.depth === 2 ? (
                                <Link href={`/treatments/${node.items[0].id}`} target="_blank" rel="noopener" underline="hover">
                                    {node.items[0].clientNumber}
                                </Link>
                            ) : (
                                node.label
                            )
                        }
                        isLoading={waitingTreatmentsResult.isLoading}
                        isError={waitingTreatmentsResult.isError}
                    />
                </Grid>
                <Grid item xs={12}>
                    <DataTableCard
                        title="Análise detalhada — minutos para contactar"
                        explanation={explanations.detail}
                        headerControls={
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <DashboardDateFilter
                                    label="Criação"
                                    filterKey="detailCriacao"
                                    values={detailFilterValues}
                                    onChange={setDetailFilterValue}
                                    presets={DETAIL_DATE_PRESETS}
                                />
                                <TextField
                                    select
                                    size="small"
                                    label="Minutos úteis"
                                    value={detailMinMinutes === null ? "todas" : detailMinMinutes}
                                    onChange={(event) => {
                                        const newValue = event.target.value;
                                        setDetailMinMinutes(newValue === "todas" ? null : Number(newValue));
                                    }}
                                    sx={{ minWidth: 130 }}
                                >
                                    <MenuItem value="todas">Todas</MenuItem>
                                    {DETAIL_MINUTES_THRESHOLD_OPTIONS.map((minutesOption) => (
                                        <MenuItem key={minutesOption} value={minutesOption}>
                                            &gt; {minutesOption} min
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        }
                        columns={[
                            { key: "link", label: "Lead" },
                            { key: "created", label: "Criação" },
                            { key: "firstContact", label: "1.ª tentativa de contacto" },
                            { key: "minutes", label: "Minutos úteis", isNumeric: true },
                        ]}
                        rows={detailRows.map((row) => ({
                            ...row,
                            link: (
                                <Link href={row.link} target="_blank" rel="noopener" underline="hover">
                                    {row.link.replace(CRM_LEAD_LINK_PREFIX, "lead ")}
                                </Link>
                            ),
                        }))}
                        totalsRow={{ link: "Média geral", created: "", firstContact: "", minutes: detailAverage === null ? "—" : detailAverage.toFixed(2) }}
                        maxBodyHeight={420}
                        isLoading={detailLeadsResult.isLoading}
                        isError={detailLeadsResult.isError}
                    />
                </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 1, fontWeight: 700 }}>
                Produtividade da equipa de receção
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                    <DataTableCard
                        title="Apoio ao cliente — marcações vs realizadas por pessoa"
                        explanation={explanations.bookingsByPerson}
                        columns={[
                            { key: "fullName", label: "Pessoa" },
                            { key: "marcadas", label: "Marcadas", isNumeric: true },
                            { key: "realizadas", label: "Realizadas", isNumeric: true },
                            { key: "comparenciaPct", label: "% Comparência", isNumeric: true },
                        ]}
                        rows={bookingRows.map((row) => ({
                            fullName: row.fullName,
                            marcadas: row.marcadas,
                            realizadas: row.realizadas,
                            comparenciaPct: row.comparenciaPct === null ? "—" : `${row.comparenciaPct.toFixed(1)}%`,
                        }))}
                        totalsRow={{
                            fullName: "Total",
                            marcadas: bookingTotals.marcadas,
                            realizadas: bookingTotals.realizadas,
                            comparenciaPct: bookingTotals.comparenciaPct === null ? "—" : `${bookingTotals.comparenciaPct.toFixed(1)}%`,
                        }}
                        maxBodyHeight={420}
                        isLoading={bookingTableLoading}
                        isError={bookingsByPersonResult.isError}
                    />
                </Grid>
                <Grid item xs={12} md={7}>
                    <DataTableCard
                        title="Minutos e chamadas para efetuar uma marcação"
                        explanation={explanations.callsByPerson}
                        columns={[
                            { key: "fullName", label: "Utilizador" },
                            { key: "chamadas", label: "Chamadas", isNumeric: true },
                            { key: "totalMinutos", label: "Total de minutos", isNumeric: true },
                            { key: "marcacoes", label: "Marcações", isNumeric: true },
                            { key: "chamadasPorMarcacao", label: "Chamadas/marcação", isNumeric: true },
                            { key: "minutosPorMarcacao", label: "Minutos/marcação", isNumeric: true },
                        ]}
                        rows={callRows.map((row) => ({
                            fullName: row.fullName,
                            chamadas: row.chamadas > 0 ? row.chamadas : "",
                            totalMinutos: row.chamadas > 0 ? row.totalMinutos.toFixed(2) : "",
                            marcacoes: row.marcacoes > 0 ? row.marcacoes : "",
                            chamadasPorMarcacao: row.chamadasPorMarcacao === null ? "" : row.chamadasPorMarcacao.toFixed(2),
                            minutosPorMarcacao: row.minutosPorMarcacao === null ? "" : row.minutosPorMarcacao.toFixed(2),
                        }))}
                        totalsRow={{
                            fullName: "Total",
                            chamadas: callTotals.chamadas,
                            totalMinutos: callTotals.totalMinutos.toFixed(2),
                            marcacoes: callTotals.marcacoes,
                            chamadasPorMarcacao: callTotals.chamadasPorMarcacao === null ? "" : callTotals.chamadasPorMarcacao.toFixed(2),
                            minutosPorMarcacao: callTotals.minutosPorMarcacao === null ? "" : callTotals.minutosPorMarcacao.toFixed(2),
                        }}
                        maxBodyHeight={420}
                        isLoading={callTableLoading}
                        isError={callsByOperatorResult.isError}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {!isOwnProductivityView && (
                    <Grid item xs={12} md={4}>
                        <WidgetCard
                            title="Média diária de conversação por pessoa (período)"
                            explanation={explanations.avgConversation}
                            bodyHeight={400}
                            isLoading={avgConversationLoading}
                            isError={callsPeriodByDayResult.isError}
                        >
                            <StackedBarChart
                                points={avgConversationPoints}
                                series={avgConversationSeries}
                                categoryKey="person"
                                height={400}
                                valueFormatter={(value) => `${value.toFixed(2)} h`}
                                showBarLabels
                            />
                        </WidgetCard>
                    </Grid>
                )}
                <Grid item xs={12} md={isOwnProductivityView ? 12 : 8}>
                    <WidgetCard
                        title="Horas de conversação vs horário agendado (por pessoa)"
                        explanation={explanations.conversationHours}
                        bodyHeight={400}
                        isLoading={conversationLoading}
                        isError={conversationCallsResult.isError || conversationSchedulesResult.isError || callTeamResult.isError}
                    >
                        <ScheduleCallsBarChart persons={conversation.persons} points={conversation.points} height={400} />
                    </WidgetCard>
                </Grid>
            </Grid>
        </DashboardPage>
    );
};
